import styled, { css } from "styled-components";
import { BoardType } from '../helpers/BoardHelper';
import { PlayerId } from "./Types";

type PropsType = {
    board: BoardType;
    onClick: (x: number, y: number) => void;
}

export default ({ board, onClick }: PropsType): JSX.Element => (
    <BoardContainer>
        {board.map((row, y) =>
            <BoardRow key={y}>
                {row.map((item, x) =>
                    <TileButton
                        key={`${x.toString()}-${y.toString()}`}
                        highlight={item.highlight}
                        markHuman={item.state === PlayerId.Human}
                        markCPU={item.state === PlayerId.CPU}
                        onMouseDown={() => onClick(x, y)}
                    >
                        {item.strike === 1 ? <StrikeHorizontal /> : null}
                        {item.strike === 2 ? <StrikeDiagLeft /> : null}
                        {item.strike === 3 ? <StrikeVertical /> : null}
                        {item.strike === 4 ? <StrikeDiagRight /> : null}
                    </TileButton>
                )}
            </BoardRow>
        )}
    </BoardContainer>
);

const MOBILE_MIN_DPI = "144dpi";
const GRID_COLOR = "#bfbfbf";
const GRID_SHADOW = `0 0 0 1px ${GRID_COLOR}`;
const HDPI_GRID_COLOR = "#808080";
const HDPI_GRID_SHADOW = `0 0 0 0.5px ${HDPI_GRID_COLOR}`;

const BoardContainer = styled.div`
    position: relative;
    width: 100%;
    box-sizing: border-box;
    background: white;

    box-shadow: ${GRID_SHADOW};
    @media (min-resolution: ${MOBILE_MIN_DPI}) {
        box-shadow: ${HDPI_GRID_SHADOW};
    }
`;

const BoardRow = styled.div`
    display: flex;
    justify-content: space-around;
`;

const TileButton = styled.button<{
    highlight: boolean;
    markHuman: boolean;
    markCPU: boolean;
}>`
    position: relative;
    flex: 1 0 auto;
    height: auto;
    padding: 0;
    border: none;
    background: transparent;
    
    box-shadow: inset ${GRID_SHADOW};
    @media (min-resolution: ${MOBILE_MIN_DPI}) {
        box-shadow: inset ${HDPI_GRID_SHADOW};
    }
    
    ${({ markHuman, highlight }) => markHuman && css`
        background-image: url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'><path fill='%23BB0000' d='M301 256l137-137c12-12 12-33 0-45c-12-12-33-12-45 0L256 211L119 73c-12-12-33-12-45 0c-12 12-12 33 0 45L211 256l-137 137c-12 12-12 33 0 45C80 445 88 448 96 448s16-3 23-9L256 301l137 137C400 445 408 448 416 448s16-3 23-9c12-12 12-33 0-45L301 256z'/></svg>");
        ${highlight && "background-color: #ffe5e5;"}
    `};
    
    ${({ markCPU, highlight }) => markCPU && css`
        background-image: url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'><path fill='%230000BB' d='M256 64C150 64 64 150 64 256s86 192 192 192s192-86 192-192S362 64 256 64zM256 384c-71 0-128-57-128-128c0-71 57-128 128-128s128 57 128 128C384 327 327 384 256 384z'/></svg>");
        ${highlight && "background-color: #e5e5ff;"}
    `};
    
    background-position: center center;
    background-size: 100% 100%;
    background-repeat: repeat;
    
    &:before {
        content: '';
        float: left;
        padding-top: 100%;
    }
    
    &:focus {
        outline: none;
    }
`;

const StrikeBase = styled.div`
    display: block;
    position: absolute;
    top: -6%;
    right: -6%;
    bottom: -6%;
    left: -6%;
    background-position: 0 0;
    background-size: 100% 100%;
    z-index: 1;
`;

const StrikeHorizontal = styled(StrikeBase)`
    background-image: url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'><path d='M0 224L512 224 512 288 0 288zM480 288H32c-18 0-32-14-32-32s14-32 32-32h448c18 0 32 14 32 32S498 288 480 288z'/></svg>");
`;

const StrikeVertical = styled(StrikeHorizontal)`
    transform: rotate(90deg);
`;

const StrikeDiagLeft = styled(StrikeBase)`
    background-image: url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'><path d='M480 512c-8 0-16-3-23-9l-448-448c-12-12-12-33 0-45c12-12 33-12 45 0l448 448c12 12 12 33 0 45C496 509 488 512 480 512z'/></svg>");
`;

const StrikeDiagRight = styled(StrikeDiagLeft)`
    transform: rotate(90deg);
`;